// node_modules
import axios, { AxiosRequestConfig } from "axios";
import { LoggingConstants } from "Constants";
// Helpers
import { AxiosHelperSingleton, PendoHelperSingleton } from "Helpers";
// Types
import { TCase, TCaseHeaderUpdateInfoDTO, TSavedQueryTempDTO } from "Types";

export class CaseController {
    public async searchAsync(query: string): Promise<TCase[]> {
        // Log the retrieval of info of the current current case
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}CaseSearch`, {
            query: query
        });

        const axiosRequestConfig: AxiosRequestConfig = {
            params: {
                query
            }
        };
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/cases`, axiosRequestConfig)).data;
    }

    public async getCaseByIdAsync(id: number): Promise<TCase> {
        // Log the retrieval of info of the current current case
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}CaseGetById`, {
            caseId: id
        });
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/cases/${id}`)).data;
    }

    public async getCaseSavedQueriesAsync(caseId: number): Promise<TSavedQueryTempDTO[]> {
        // Log the retrieval of the saved queries of the current case
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}CaseGetSavedQueries`, {
            caseId: caseId
        });

        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/savedquery/case/${caseId}`)).data;
    }

    public async updateHeaderInfo(caseId: number, title: string | null | undefined, image: File | Blob | null | undefined, doDeleteImage: boolean): Promise<TCaseHeaderUpdateInfoDTO> {
        const formData = new FormData();
        if (title) formData.append("title", title);
        if (image) formData.append("image", image);
        if (doDeleteImage) formData.append("doDeleteImage", doDeleteImage.toString());

        const result = await axios.post<TCaseHeaderUpdateInfoDTO>(`${AxiosHelperSingleton.getServerBaseURL()}api/cases/${caseId}/headerinfo`, formData);
        return result.data;
    }

    public async updateDeliveryType(caseId: number, deliveryType: string): Promise<boolean> {
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/cases/${caseId}/deliverytype?type=${deliveryType}`);
        return result.status === 200;
    }
    
    public async updateCaseSolutionTypeAsync(caseId: number, newCaseSolutionType: string): Promise<boolean> {
        const formData = new FormData();
        formData.append("name", newCaseSolutionType);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/MeansName/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseTagTypeAsync(caseId: number, newCaseTagType: number): Promise<boolean> {
        const formData = new FormData();
        formData.append("TagType", `${newCaseTagType}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/CaseTagType/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseNotesAsync(caseId: number, newCaseNotes: string): Promise<boolean> {
        const formData = new FormData();
        formData.append("CaseID", `${caseId}`);
        formData.append("CaseNotes", `${newCaseNotes}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/NewEdit/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseScopeAsync(caseId: number, newCaseScope: number): Promise<boolean> {
        const formData = new FormData();
        formData.append("CaseID", `${caseId}`);
        formData.append("Scope", `${newCaseScope}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/NewEdit/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseChallengeAsync(caseId: number, newCaseChallenge: string): Promise<boolean> {
        const formData = new FormData();
        formData.append("ChallengeDescription", `${newCaseChallenge}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/NewEdit/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseIdealOutcomeAsync(caseId: number, newCaseIdealOutcome: string): Promise<boolean> {
        const formData = new FormData();
        formData.append("IdealOutcome", `${newCaseIdealOutcome}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/NewEdit/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseMVPOutcomeAsync(caseId: number, newCaseMVPOutcome: string): Promise<boolean> {
        const formData = new FormData();
        formData.append("MvpOutcome", `${newCaseMVPOutcome}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/NewEdit/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateCaseDeliveryIntakeOverrideAsync(caseId: number, newCaseDeliveryIntakeOverride: string): Promise<boolean> {
        const formData = new FormData();
        formData.append("DeliveryIntakeNotes", `${newCaseDeliveryIntakeOverride}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/NewEdit/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateIsDynamicDeliveryAsync(caseId: number, isDynamicDelivery: boolean): Promise<boolean> {
        const formData = new FormData();
        formData.append("isDynamicDelivery", `${isDynamicDelivery}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/ToggleIsDynamicDelivery/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }

    public async updateIsDeliveryRedesignAsync(caseId: number, isDeliveryRedesign: boolean): Promise<boolean> {
        const formData = new FormData();
        formData.append("isDeliveryRedesign", `${isDeliveryRedesign}`);
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/ToggleIsDeliveryRedesign/${caseId}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } })).status === 200;
    }
}

export const CaseControllerSingleton = new CaseController();